import { Q } from "@nozbe/watermelondb";
import { database } from "../index";
import {
  BUSINESS_SCHEMA,
  CUSTOMER_SCHEMA,
  PAYMENT_SCHEMA,
  INVOICE_SCHEMA,
} from "../schema";
import { NEW_SORT } from "../../constants";

const payment = database.collections.get(PAYMENT_SCHEMA);

export const getPaymentByID = async (id) => await payment.find(id);

export const getImagesToSync = async () =>
  await payment.query(Q.where("image_url", Q.like("%data:image%"))).fetch();

export const getPaymentCount = async () => await payment.query().fetchCount();

export const getPaymentsByCustomer = async (id) => {
  await payment.query(Q.on(CUSTOMER_SCHEMA, "id", id)).fetch();
};

export const getFiltersPaymentsByCustomer = async (id, filters) => {
  let filterQuery = [];

  if (filters) {
    let { type, date, amount,keywords } = filters;

    const start = date?.startDate;
    const end = date?.endDate;

    // TYPE FILTER (example: "given", "received")
    if (type?.length > 0) {
      const conditions = [];

      if (type.includes("Given")) {
        conditions.push(Q.where("payment_mode", "Udhaar"));
      }

      if (type.includes("Received")) {
        conditions.push(Q.where("payment_mode", "Jama"));
      }

      if (conditions.length > 0) {
        filterQuery.push(Q.or(...conditions));
      }
    }

    // DATE FILTER
    if (start) {
      filterQuery.push(Q.where("transacted_at", Q.gte(start.getTime())));
    }

    if (end) {
      const endOfDay = new Date(end);
      endOfDay.setHours(23, 59, 59, 999);

      filterQuery.push(Q.where("transacted_at", Q.lte(endOfDay.getTime())));
    }

    // AMOUNT RANGE
    if (amount?.min !== undefined && amount?.min !== "") {
      filterQuery.push(Q.where("amount", Q.gte(amount.min)));
    }

    if (amount?.max !== undefined && amount?.max !== "") {
      filterQuery.push(Q.where("amount", Q.lte(amount.max)));
    }

    if(keywords){
      filterQuery.push(Q.where("udhaar_note",Q.like(`%${Q.sanitizeLikeString(keywords)}%`)))
    }

  }

  // BASE QUERY
  let query = payment.query(Q.on(CUSTOMER_SCHEMA, "id", id), ...filterQuery);

  // SORTING (outside filters so it works always)
  if (filters?.sort) {
    switch (filters.sort) {
      case NEW_SORT.amountHighToLow:
        query = query.extend(Q.sortBy("amount", Q.desc));
        break;

      case NEW_SORT.amountLowToHigh:
        query = query.extend(Q.sortBy("amount", Q.asc));
        break;

      case NEW_SORT.nameAToZ:
        query = query.extend(Q.sortBy("udhaar_note", Q.asc));
        break;

      case NEW_SORT.nameZToA:
        query = query.extend(Q.sortBy("udhaar_note", Q.desc));
        break;

      case NEW_SORT.Oldest:
        query = query.extend(Q.sortBy("transacted_at", Q.asc));
        break;

      case NEW_SORT.Newest:
      default:
        query = query.extend(Q.sortBy("transacted_at", Q.desc));
        break;
    }
  } else {
    query = query.extend(Q.sortBy("transacted_at", Q.desc));
  }

  return await query.fetch();
};

export const getAllPaymentsByCustomer = async (id, sort) => {
  // BASE QUERY
  let query = payment.query(Q.on(CUSTOMER_SCHEMA, "id", id));

  // SORTING (outside filters so it works always)
  if (sort) {
    switch (sort) {
      case NEW_SORT.amountHighToLow:
        query = query.extend(Q.sortBy("amount", Q.desc));
        break;

      case NEW_SORT.amountLowToHigh:
        query = query.extend(Q.sortBy("amount", Q.asc));
        break;

      case NEW_SORT.nameAToZ:
        query = query.extend(Q.sortBy("udhaar_note", Q.asc));
        break;

      case NEW_SORT.nameZToA:
        query = query.extend(Q.sortBy("udhaar_note", Q.desc));
        break;

      case NEW_SORT.Oldest:
        query = query.extend(Q.sortBy("transacted_at", Q.asc));
        break;

      case NEW_SORT.Newest:
      default:
        query = query.extend(Q.sortBy("transacted_at", Q.desc));
        break;
    }
  } else {
    query = query.extend(Q.sortBy("transacted_at", Q.desc));
  }

  return await query.fetch();
};

// Returns only {id, amount, payment_mode} for balance calculation — no model instantiation
export const getLeanPaymentsForBalance = async (id, sort) => {
  let query = payment.query(Q.on(CUSTOMER_SCHEMA, "id", id));

  if (sort) {
    switch (sort) {
      case NEW_SORT.amountHighToLow:
        query = query.extend(Q.sortBy("amount", Q.desc));
        break;
      case NEW_SORT.amountLowToHigh:
        query = query.extend(Q.sortBy("amount", Q.asc));
        break;
      case NEW_SORT.nameAToZ:
        query = query.extend(Q.sortBy("udhaar_note", Q.asc));
        break;
      case NEW_SORT.nameZToA:
        query = query.extend(Q.sortBy("udhaar_note", Q.desc));
        break;
      case NEW_SORT.Oldest:
        query = query.extend(Q.sortBy("transacted_at", Q.asc));
        break;
      case NEW_SORT.Newest:
      default:
        query = query.extend(Q.sortBy("transacted_at", Q.desc));
        break;
    }
  } else {
    query = query.extend(Q.sortBy("transacted_at", Q.desc));
  }

  const raw = await query.unsafeFetchRaw();
  return raw.map((r) => ({ id: r.id, amount: r.amount, payment_mode: r.payment_mode }));
};

export const observeActivePaymentCount = () =>
  payment.query(Q.on(BUSINESS_SCHEMA, "is_active", true)).observeCount();

export const observePaymentsByCustomer = (id) =>
  payment.query(Q.on(CUSTOMER_SCHEMA, "id", id)).observe();

export const observePaymentsById = (id) =>
  payment.query(Q.where("id", id)).observe();

export const observeBusinessPayments = (id) =>
  payment.query(Q.where("business_id", id)).observe();

export const getPaymentsByBusiness = async (id) =>
  await payment.query(Q.on(BUSINESS_SCHEMA, "id", id)).fetch();

export const getPaymentsByInvoiceExternal = async (id, external_type) =>
  await payment
    .query(
      Q.on(INVOICE_SCHEMA, "id", id),
      Q.where("external_type", external_type),
    )
    .fetch();

export const getFilteredPayments = async (id, filters) => {
  let data;
  if (filters?.start && filters?.end) {
    data = await payment
      .query(
        Q.where("business_id", id),
        Q.where("transacted_at", Q.lte(filters.end)),
        Q.where("transacted_at", Q.gte(filters.start)),
      )
      .fetch();
  } else data = await payment.query(Q.where("business_id", id)).fetch();

  return data;
};

export const getAmount = async (id) => {
  let payments = await payment.query(Q.on(CUSTOMER_SCHEMA, "id", id)).fetch();

  let totalGiven = payments.reduce((value, data) => {
    return data.payment_mode == "Udhaar" ? (value += data.amount) : 0;
  }, 0);

  let totalTaken = payments.reduce((value, data) => {
    return data.payment_mode == "Jama" ? (value += data.amount) : 0;
  }, 0);

  return [totalTaken, totalGiven];
};

export const getPaymentByCustomerID = async (id) => {
  let payments = await payment.query(Q.on(CUSTOMER_SCHEMA, "id", id)).fetch();
  let total = payments.reduce((value, data) => {
    return data.payment_mode == "Udhaar"
      ? (value += data.amount)
      : (value -= data.amount);
  }, 0);

  return total;
};

export const deletePaymentByID = async (id) => {
  const getPayment = await payment.find(id);
  await getPayment.markAsDeleted();
};

export const bulkUpdatePayments = async (payments, data) => {
  await database.write(async () => {
    const allRecords = await prepareUpdation(payments, data);
    await database.batch(...allRecords);
  });
};

const prepareUpdation = (payments, data) => {
  return payments.map((payment) => {
    try {
      return payment.prepareUpdate((pay) => {
        pay.amount = data.amount;
        pay.udhaar_note = data.note;
        // pay.created_at = data.date
        pay.transacted_at = data.date;
        pay.image_url = data.image_url;
      });
    } catch (err) {
    }
  });
};

export const bulkCreatePayments = async (
  business,
  customer,
  invoice,
  payments,
) => {
  await database.write(async () => {
    const allRecords = await prepareInsertion(
      business,
      customer,
      invoice,
      payments,
    );
    await database.batch(...allRecords);
  });
};

const prepareInsertion = async (business, customer, invoice, payments) => {
  return await Promise.all(
    payments.map(async (field) => {
      try {
        return payment.prepareCreate((pay) => {
          pay._raw.id = field?.uid;
          pay.customer.set(customer);
          pay.business.set(business);
          pay.invoice.set(invoice);
          pay.amount = field?.amount;
          pay.udhaar_note = field?.note;
          pay.payment_mode = field?.payment_mode;
          pay.transacted_at = field?.date;
          pay.image_url = field?.image_url;
          pay.external_type = field?.external_type;
          pay.type = field?.type;
        });
      } catch (err) {
      }
    }),
  );
};

export const bulkDeletePaymentsByBusiness = async (business_id) => {
  await database.write(async () => {
    let payments = await getPaymentsByBusiness(business_id);
    const allRecords = await prepareDeletion(payments);
    await database.batch(...allRecords);
  });
};

export const bulkDeletePayments = async (customer_id) => {
  await database.write(async () => {
    let payments = await getPaymentsByCustomer(customer_id);
    if (payment?.length) {
      const allRecords = await prepareDeletion(payments);
      await database.batch(...allRecords);
    }
  });
};

export const bulkDeletePaymentsByInvoice = async (payments) => {
  await database.write(async () => {
    const allRecords = await prepareDeletion(payments);
    await database.batch(...allRecords);
  });
};

const prepareDeletion = async (payments) => {
  return await Promise.all(
    payments.map(async (payment) => {
      try {
        return payment.prepareMarkAsDeleted();
      } catch (err) {
      }
    }),
  );
};

export const bulkDeletePaymentsV2 = async (payments) => {
  if (payments?.length) {
    await database.write(async () => {
      const allRecords = prepareDeletionV2(payments);
      await database.batch(...allRecords);
    });
  }
};

const prepareDeletionV2 = (payments) => {
  return payments.map((payment) => {
    try {
      return payment.prepareMarkAsDeleted();
    } catch (err) {
    }
  });
};

export const getAttachmentsToPaymentSync = async () => {
  try {
    let records = await payment
      .query(Q.where("attachment", Q.notLike("[]")))
      .fetch();
    const filteredRecords = records.filter((record) => {
      const attachments = record.attachment || [];
      return attachments.some((attachment) =>
        attachment?.file?.startsWith?.("data"),
      );
    });
    return filteredRecords;
  } catch (error) {
    console.error("Error fetching records:", error);
  }
};